import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, ExternalLink, TrendingUp, Eye, MousePointer, Loader2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchQueriesInsights = ({ websiteId, token, period, startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [queries, setQueries] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSearchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // First check if Search Console is connected
        const statusRes = await axios.get(`${API}/integrations/search-console/status`, {
          params: { website_id: websiteId, token }
        });
        
        if (!statusRes.data.connected || !statusRes.data.linked_property) {
          setConnected(false);
          setLoading(false);
          return;
        }
        
        setConnected(true);
        
        // Fetch search data
        const params = { website_id: websiteId, token };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        
        const dataRes = await axios.get(`${API}/integrations/search-console/data`, { params });
        setQueries(dataRes.data.queries || []);
      } catch (err) {
        console.error('Failed to fetch search data:', err);
        if (err.response?.status === 400) {
          setConnected(false);
        } else {
          setError(err.response?.data?.detail || 'Failed to load search data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSearchData();
  }, [websiteId, token, period, startDate, endDate]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="w-4 h-4" />
            Search Queries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!connected) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="w-4 h-4" />
            Search Queries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Link2 className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Connect Google Search Console to see what search queries bring visitors to your site.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/settings/${websiteId}/integrations`)}
            >
              Connect Search Console
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="w-4 h-4" />
            Search Queries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-red-600">
            <p className="text-sm">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="w-4 h-4" />
            Search Queries
          </CardTitle>
          <a
            href="https://search.google.com/search-console"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-indigo-600 flex items-center gap-1"
          >
            View in Search Console
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </CardHeader>
      <CardContent>
        {queries.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <p className="text-sm">No search query data available for this period.</p>
            <p className="text-xs mt-1">Data may take 2-3 days to appear in Search Console.</p>
          </div>
        ) : (
          <div className="space-y-0">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 px-2 py-2 text-xs font-medium text-gray-500 border-b">
              <div className="col-span-6">Query</div>
              <div className="col-span-2 text-right">Clicks</div>
              <div className="col-span-2 text-right">Impressions</div>
              <div className="col-span-2 text-right">Position</div>
            </div>
            
            {/* Rows */}
            <div className="max-h-80 overflow-y-auto">
              {queries.slice(0, 20).map((query, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 px-2 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                >
                  <div className="col-span-6 truncate text-gray-900" title={query.query}>
                    {query.query}
                  </div>
                  <div className="col-span-2 text-right font-medium text-indigo-600">
                    {query.clicks.toLocaleString()}
                  </div>
                  <div className="col-span-2 text-right text-gray-600">
                    {query.impressions.toLocaleString()}
                  </div>
                  <div className="col-span-2 text-right text-gray-600">
                    {query.position.toFixed(1)}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Summary */}
            <div className="pt-3 mt-2 border-t flex justify-between text-xs text-gray-500">
              <span>Showing top {Math.min(20, queries.length)} of {queries.length} queries</span>
              <div className="flex gap-4">
                <span className="flex items-center gap-1">
                  <MousePointer className="w-3 h-3" />
                  {queries.reduce((sum, q) => sum + q.clicks, 0).toLocaleString()} clicks
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {queries.reduce((sum, q) => sum + q.impressions, 0).toLocaleString()} impressions
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SearchQueriesInsights;
